import {
  FLEET_DEFAULT_HOST_LIMITS,
  FLEET_HEALTH_WARN_PERCENT,
  type FleetHostHealth,
  type FleetHostLimits,
  type FleetHostThrottle,
  type FleetUsedOfTotal,
} from '@immo/api-contract';

export type Figure<T> = { readonly kind: 'missing' } | ({ readonly kind: 'value' } & T);

/** Keys relative to `pages.admin.fleet.health`. */
export type ThrottleReasonKey =
  | 'reason-temperature'
  | 'reason-load'
  | 'reason-memory-pressure'
  | 'reason-thermal-pressure'
  | 'temperature'
  | 'load'
  | 'pressure';

const MISSING = { kind: 'missing' } as const;

// The epsilon keeps a binary-float product such as 12.999999999 from flooring a true 1.3 down to 1.2.
export function floorTenth(x: number): number {
  return Math.floor(x * 10 + 1e-9) / 10;
}

/** The load per core at which a load bar is full. */
export const LOAD_BAR_SCALE = 2;

export function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/** floor(ratio × 100) for a series point; the epsilon keeps 0.29 × 100 = 28.999… from flooring to 28. */
export function ratioPercent(ratio: number): number {
  return Math.floor(ratio * 100 + 1e-9);
}

/** floor(used / total × 100); a total of 0 (no swap configured) is 0 %. */
export function percentOf(
  figure: FleetUsedOfTotal | null,
): Figure<{ percent: number; usedBytes: number; totalBytes: number }> {
  if (figure === null) return MISSING;
  const { usedBytes, totalBytes } = figure;

  if (totalBytes === 0) return { kind: 'value', percent: 0, usedBytes, totalBytes };

  return { kind: 'value', percent: Math.floor((usedBytes * 100) / totalBytes), usedBytes, totalBytes };
}

/** Inclusive 90, on the floored percent (FLEET_HEALTH_WARN_PERCENT). */
export function isNearlyFull(percent: number): boolean {
  return percent >= FLEET_HEALTH_WARN_PERCENT;
}

/** The limits to mark: the reading's own, else FLEET_DEFAULT_HOST_LIMITS. */
export function limitsOf(health: FleetHostHealth | null): FleetHostLimits {
  return health?.limits ?? FLEET_DEFAULT_HOST_LIMITS;
}

export function temperatureOf(health: FleetHostHealth | null): Figure<{ degrees: number; overLimit: boolean }> {
  if (health === null || health.cpuTemperatureC === null) return MISSING;
  const degrees = Math.floor(health.cpuTemperatureC);

  return { kind: 'value', degrees, overLimit: degrees >= limitsOf(health).temperatureC.throttleAt };
}

/** load1m / cpuCores floored to one decimal; missing when either is null or cores is 0. */
export function loadOf(health: FleetHostHealth | null): Figure<{ factor: number; overLimit: boolean }> {
  if (health === null || health.load1m === null || health.cpuCores === null || health.cpuCores === 0) return MISSING;
  const factor = floorTenth(health.load1m / health.cpuCores);

  return { kind: 'value', factor, overLimit: factor >= limitsOf(health).loadPerCore.throttleAt };
}

export function pressureOf(health: FleetHostHealth | null): Figure<{ percent: number; overLimit: boolean }> {
  if (health === null || health.memoryPressurePercent === null) return MISSING;
  const percent = Math.floor(health.memoryPressurePercent);

  return { kind: 'value', percent, overLimit: percent >= limitsOf(health).memoryPressurePercent.throttleAt };
}

const FACTOR_FORMAT = { minimumFractionDigits: 1, maximumFractionDigits: 1 } as const;

/** "1,3" — de-DE, exactly one decimal. */
export function formatFactor(factor: number): string {
  return factor.toLocaleString('de-DE', FACTOR_FORMAT);
}

/**
 * Whole GB at 10 GB and above ("412", "1.000"), one decimal below ("2,5"); 1 GB = 1024³ bytes.
 * Returns the number only: render it through `health.gigabytes`, which adds " GB".
 */
export function formatGigabytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;

  if (gb >= 10) return Math.floor(gb).toLocaleString('de-DE');

  return formatFactor(floorTenth(gb));
}

/** The i18n key and values for a throttle reason. */
export function throttleReason(throttle: FleetHostThrottle): {
  readonly key: ThrottleReasonKey;
  readonly values: Record<string, string>;
} {
  const { reason, value } = throttle;

  switch (reason) {
    case 'temperature':
      return value === null
        ? { key: 'temperature', values: {} }
        : { key: 'reason-temperature', values: { degrees: String(Math.floor(value)) } };
    case 'load':
      return value === null
        ? { key: 'load', values: {} }
        : { key: 'reason-load', values: { factor: formatFactor(floorTenth(value)) } };
    case 'memory-pressure':
      return value === null
        ? { key: 'pressure', values: {} }
        : { key: 'reason-memory-pressure', values: { percent: String(Math.floor(value)) } };
    case 'thermal-pressure':
      return { key: 'reason-thermal-pressure', values: {} };
  }

  const unreachable: never = reason;

  return unreachable;
}
